import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { OrderListTable } from "@/components/order/table";
import { useGetAllOrders } from "@/hooks/api/order/useGetAllOrders";
import { useDebounce } from "@/hooks/useDebounce";
import { AppPage } from "@/layouts/page";
import { AttributionFilters } from "@/types";

export type OrderFilterValues = AttributionFilters & {
  orderStatus?: string;
  paymentStatus?: string;
  dateFilter?: string;
};

export const ORDER_PAGE_SIZES = [10, 20, 50, 100];
const PHONE_LIKE = /^\+?[\d\s-]{5,}$/;

const OrdersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<OrderFilterValues>({});
  const [pageSize, setPageSize] = useState(20);
  const debouncedSearch = useDebounce(search.trim(), 400);

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1") || 1);

  const changePage = (next: number) => {
    const params = new URLSearchParams(searchParams);

    params.set("page", next.toString());
    setSearchParams(params);
  };

  // A phone number is searched as one, anything else as a customer name.
  const query = useMemo(
    () => ({
      ...filters,
      ...(PHONE_LIKE.test(debouncedSearch)
        ? { userPhone: debouncedSearch.replace(/[\s-]/g, "") }
        : { userName: debouncedSearch }),
      page,
      limit: pageSize,
    }),
    [filters, debouncedSearch, page, pageSize],
  );

  const { data: orders, isFetching } = useGetAllOrders(query);

  // Any change to the search or the filters starts again from page one.
  const resetKey = JSON.stringify([filters, debouncedSearch, pageSize]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }
    if (page !== 1) changePage(1);
  }, [resetKey]);

  const total = orders?.total ?? 0;

  return (
    <AppPage
      subtitle={
        orders ? `${total.toLocaleString()} orders in total` : undefined
      }
      title="Orders"
    >
      <OrderListTable
        changePage={changePage}
        filters={filters}
        isLoading={isFetching && !orders}
        items={orders?.data ?? []}
        limit={pageSize}
        page={page}
        search={search}
        totalOrders={total}
        onFiltersChange={setFilters}
        onLimitChange={setPageSize}
        onSearchChange={setSearch}
      />
    </AppPage>
  );
};

export default OrdersPage;
